import { Injectable } from '@angular/core';
import { PropertySearchResponseDto } from '../dto/property.search.response.dto';
import { MultimediaFeature } from '../dto/multimedia.feature.response.dto';
import {
    PropertyDetailVM,
    GalleryImageVM,
    MapLocationVM,
    PropertyDocumentVM,
} from '../models/property-detail.vm';

/**
 * Transforma los DTO crudos del detalle en ViewModels listos para la UI.
 * Única fuente de la presentación del detalle (precio, iconos de papeles,
 * aplanado de galería, parseo de coordenadas). Sin estado ni HTTP.
 */
@Injectable({ providedIn: 'root' })
export class PropertyDetailMapper {

    /** optionCode del papel → ícono Material. */
    private static readonly PAPER_ICON: Record<string, string> = {
        FOLIO_REAL: 'description',
        PLAN_CATASTRAL: 'map',
        CONSTRUCTION_PERMIT: 'gavel',
    };

    private static readonly PRICE_FORMAT = new Intl.NumberFormat('es-BO', {
        maximumFractionDigits: 0,
    });

    toDetailVM(dto: PropertySearchResponseDto | null | undefined): PropertyDetailVM | null {
        if (!dto) {
            return null;
        }
        return {
            propertyId: dto.propertyId,
            segmentId: dto.segmentId ?? null,
            badge: dto.adLabel ?? '',
            header: {
                propertyName: dto.propertyName ?? '',
                propertyAddress: dto.propertyAddress ?? '',
                zoneName: dto.zoneName ?? '',
                cityName: dto.cityName ?? '',
                segmentLabel: dto.segmentLabel ?? '',
                segmentImagePath: dto.segmentImagePath ?? '',
                adLabel: dto.adLabel ?? '',
                adPrice: this.toPrice(dto.adPrice),
                optUseStatus: dto.optUseStatus ?? '',
            },
            summary: {
                surface: dto.propertySurface || null,
                rooms: dto.roomsNumber ?? null,
                baths: dto.bathroomsNumber ?? null,
            },
        };
    }

    /**
     * Aplana TODAS las imágenes de todas las categorías en una sola lista.
     * (Corrige el bug previo: el componente pasaba un array de arrays.)
     */
    toGallery(features: MultimediaFeature[] | null | undefined): GalleryImageVM[] {
        const images: GalleryImageVM[] = [];
        (features ?? []).forEach((feature) => {
            (feature.fileList ?? [])
                .filter((file) => file.ctFileType === 'IMAGE_FILE')
                .forEach((file) => {
                    images.push({
                        id: file.fileId,
                        name: file.fileTitle || feature.featureLabel,
                        url: file.fileUrl,
                    });
                });
        });
        return images;
    }

    toLocation(loc: any): MapLocationVM | null {
        if (!loc) {
            return null;
        }
        const lat = parseFloat(loc.latitude);
        const lng = parseFloat(loc.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            return null;
        }
        return { lat, lng, radius: 100 };
    }

    toDocuments(papers: any[] | null | undefined): PropertyDocumentVM[] {
        return (papers ?? []).map((paper) => ({
            name: paper.label || paper.catalogLabel?.optionName || '',
            status: paper.selectedStatus?.optionCode === 'VALID' ? 'VALID' : 'MISSING',
            icon: this.iconForPaper(paper.catalogLabel?.optionCode),
        } as PropertyDocumentVM));
    }

    private iconForPaper(code: string | undefined): string {
        return PropertyDetailMapper.PAPER_ICON[code ?? ''] ?? 'insert_drive_file';
    }

    private toPrice(adPrice: string | null | undefined): string {
        if (!adPrice) {
            return '';
        }
        const value = Number(adPrice);
        return Number.isFinite(value)
            ? `Bs. ${PropertyDetailMapper.PRICE_FORMAT.format(value)}`
            : `Bs. ${adPrice}`;
    }
}
